export interface AutoSyncVehicleOptions {
  colorId?: number;
  suspension?: boolean;
}

export type AutoSyncPartLoader = {
  vehicleOptions?: AutoSyncVehicleOptions;
  load: (visualizer: AutoSyncVisualizer, autoSyncIdentifier: string) => Promise<void>;
};

// docs: https://vvs.autosyncstudio.com/express/docs/reference/classes/VvsExpress.VvsExpress.html
export type InitVvsExpressProps = AutoSyncConfig & {
  apiUrl: string;
  apiKey: string;
};

export interface AutoSyncConfig {
  el?: HTMLElement | null;
  elId?: string;
  height?: string;
  vehicleImageSize?: number;
  vehicleAngles?: number[];
  showColorPicker?: boolean;
  customCss?: string;
  widthAdjustment?: string;
}

interface Color {
  Code: string;
  Id: number;
  Img001: boolean;
  Img014: boolean;
  Img032: boolean;
  Name: string;
  Rgb1: string;
  Rgb2: string;
  ShortName: string;
  Swatch: string;
}

export interface AutoSyncVisualizer {
  loadVehicleById: (vehicleId: number, options?: AutoSyncVehicleOptions) => Promise<void>;
  loadWheelByPn: (autoSyncPartNumber: string) => Promise<void>;
  loadTireByPn: (autoSyncPartNumber: string) => Promise<void>;
  loadWheelById: (autoSyncId: string) => Promise<void>;
  loadTireById: (autoSyncId: string) => Promise<void>;
  setHeight: (height: string) => void;
  setVehicleColor: (colorId: number) => void;
  onVehicleColorChange: (callback: (color: Color) => void) => void;
}

export type VisualizeVehicleParams = {
  apiKey?: string;
  autoSyncConfig?: AutoSyncConfig;
  vehicleId?: number | string;
  autoSyncPartNumber?: string;
  partLoader?: AutoSyncPartLoader;
};

export function createAutoSyncPartLoader(isTire?: boolean, useAutoSyncId?: boolean): AutoSyncPartLoader {
  if (isTire) {
    return {
      // tires can be loaded only on vehicles with suspensions enabled.
      // https://vvs.autosyncstudio.com/express/docs/#examples/example-loading-tires-code.html
      vehicleOptions: { suspension: true },
      load: (visualizer, autoSyncIdentifier) =>
        useAutoSyncId
          ? visualizer.loadTireById(autoSyncIdentifier)
          : visualizer.loadTireByPn(autoSyncIdentifier),
    };
  }

  return {
    load: (visualizer, autoSyncIdentifier) =>
      useAutoSyncId
        ? visualizer.loadWheelById(autoSyncIdentifier)
        : visualizer.loadWheelByPn(autoSyncIdentifier),
  };
}

export function isAutoSyncVisualizer(value: unknown): value is AutoSyncVisualizer {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.loadVehicleById === 'function' &&
    typeof v.loadWheelByPn === 'function' &&
    typeof v.loadTireByPn === 'function' &&
    typeof v.loadWheelById === 'function' &&
    typeof v.loadTireById === 'function' &&
    typeof v.setHeight === 'function'
  );
}

export function visualizeVehicle({
  apiKey,
  autoSyncConfig = {},
  vehicleId,
  autoSyncPartNumber,
  partLoader = createAutoSyncPartLoader(),
}: VisualizeVehicleParams): AutoSyncVisualizer | undefined {
  if (!apiKey) {
    console.error('No api key found');
    return;
  }

  if (!vehicleId) {
    return;
  }

  const candidate = window.initVvsExpress?.({
    apiUrl: 'https://api.autosyncstudio.com/',
    apiKey,
    ...autoSyncConfig,
  });
  const visualizer = isAutoSyncVisualizer(candidate) ? candidate : undefined;

  if (!visualizer) {
    return;
  }

  const options = { ...(partLoader.vehicleOptions ?? {}) };

  // Vehicle must finish loading before tire/wheel PN — especially required for tires.
  visualizer
    .loadVehicleById(Number(vehicleId), options)
    .then(() => {
      if (!autoSyncPartNumber) {
        return;
      }

      return partLoader.load(visualizer, autoSyncPartNumber).catch((err: Error) => {
        visualizer.setHeight('0px');
        console.warn(`Failed to visualize the '${autoSyncPartNumber}' part. ${err.message}`);
      });
    })
    .catch((err: Error) => {
      visualizer.setHeight('0px');
      console.warn(`Failed to visualize the vehicle. Vehicle ID: ${vehicleId}. ${err.message}`);
    });

  return visualizer;
}
